<script lang="ts">
    import clickOutside from "@ui-utils/actions/clickOutside";
    import CN from "classnames";
    import { createEventDispatcher } from "svelte";

    import Icon from "@components/Icon.svelte";
    import ChevronIcon from "@components/icons/ChevronIcon.svelte";

    import type { ItemType } from "./types";

    export let selectClass = "";
    export let selectedClass = "";
    export let hasError = false;
    export let isDisabled = false;
    export let isFocused = false;
    export let listOpen = false;
    export let label = "";
    export let items: ItemType[];
    export let value: string | number;
    export let inputAttributes: { class?: string; name?: string } = {
        class: "",
        name: "",
    };

    $: selected = items.find((item) => item.value === value) || {
        value,
        label: "",
    };

    const dispatch = createEventDispatcher();

    const toggleList = () => {
        listOpen = !listOpen;
    };

    const handleFocus = () => {
        isFocused = true;
    };

    const handleClick = () => {
        if (isDisabled) return;
        isFocused = true;
        toggleList();
    };

    const handleClose = () => {
        isFocused = false;
        listOpen = false;
    };

    const handleChange = (e: Event) => {
        const target = e.target as HTMLSelectElement;
        dispatch("change", { value: target.value });
        listOpen = false;
    };

    const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === "Enter" && !listOpen) {
            toggleList();
        }
    };
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
    class={CN("gw-select", selectClass, {
        "gw-select--error": hasError,
        "gw-select--disabled": isDisabled,
        "gw-select--focused": isFocused,
        "gw-select--opened": listOpen,
    })}
    use:clickOutside
    on:clickOutside={handleClose}
    on:click={handleClick}
    aria-haspopup="listbox"
    aria-expanded={listOpen}
    aria-labelledby="selected-option-label"
>
    {#if label}
        <p class="gw-select__label">{label}</p>
    {/if}

    <span class="gw-select__wrapper">
        <select
            class={CN("gw-select__select", selectedClass)}
            disabled={isDisabled}
            tabindex="0"
            {value}
            on:focus={handleFocus}
            on:keydown={handleKeydown}
            on:change={handleChange}
        >
            {#each items as item}
                <option value={item.value}>{item.label}</option>
            {/each}
        </select>

        <span
            class={CN("gw-select__icon", {
                "gw-select__icon-open": listOpen,
            })}
        >
            <Icon width={12} height={12} color="#0C8CE9">
                <ChevronIcon />
            </Icon>
        </span>
    </span>

    <input type="hidden" name={inputAttributes.name} value={selected.value} />
</div>

<style lang="postcss">
    .gw-select {
        --input-border-color: rgba(38, 40, 66, 0.12);
        --input-border-color-focus: #007995;
        --input-border-radius: 2px;

        display: inline-flex;
        align-items: center;
        gap: 0.25em;
        position: relative;
        box-sizing: border-box;
        line-height: 1;

        &__wrapper {
            position: relative;
            display: inline-flex;
            align-items: center;
            float: none;
        }

        &__select {
            font-size: inherit;
            padding: 0.5rem 1.75rem 0.5rem 0;
            border: none;
            background: transparent;
            color: #0c8ce9;
            cursor: pointer;
            /* for Firefox */
            -moz-appearance: none;
            /* for Safari, Chrome, Opera */
            -webkit-appearance: none;
            z-index: 1;

            &::placeholder {
                opacity: var(--placeholderOpacity, 1);
                color: var(--placeholderColor, #78848f);
            }

            &:focus {
                outline: 2px solid var(--input-border-color-focus);
                border-radius: var(--input-border-radius);
                margin: 0;
                outline-offset: 0.3rem;
            }
        }

        &__label {
            margin: 0;
            padding: 0 0.25rem 0 0;
            line-height: 1.5;
        }

        &__icon {
            position: absolute;
            right: 2px;
            display: flex;
            align-items: center;
            transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
            color: #0c8ce9;
        }
        &__icon-open {
            transform: rotate(180deg);
        }
    }
</style>
